import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { formatWithSystemLocale } from '../../utils/dateUtils';
import CurrencyInput from "../../components/CurrencyInput";
import FormattedCurrency from "../../components/FormattedCurrency";

import { useNotification } from "../../contexts/NotificationContext";
import { Stethoscope } from "lucide-react";
import { getApiUrl } from "../../config/api";

export default function DentalCare() {
  const { id: horseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError, showWarning } = useNotification();
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState({
    exam_date: "",
    findings: "",
    treatment: "",
    recommendations: "",
    amount: ""
  });

  useEffect(() => {
    fetchRecords();
  }, []);

  // Check for edit parameter in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const editId = urlParams.get('edit');
    if (editId && records.length > 0) {
      const recordToEdit = records.find(rec => rec.id.toString() === editId);
      if (recordToEdit) {
        startEdit(recordToEdit);
        // Clean up URL by removing the edit parameter
        navigate(`/horses/${horseId}/dentalcare`, { replace: true });
      }
    }
  }, [records, location.search, navigate, horseId]);

  const fetchRecords = async () => {
    try {
      const res = await axios.get(getApiUrl(`/api/horses/${horseId}/dentalcare`), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setRecords(res.data);
    } catch (err) {
      console.error("Fetch error:", err);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    try {
      // Ensure amount defaults to 0 if empty
      const formData = {
        ...form,
        amount: form.amount || "0.00"
      };
      
      await axios.post(getApiUrl(`/api/horses/${horseId}/dentalcare`), formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setForm({ exam_date: "", findings: "", treatment: "", recommendations: "", amount: "" });
      fetchRecords();
      showSuccess("Dental care record added successfully!");
    } catch (err) {
      console.error("Submit error:", err);
      showError(err.response?.data?.error || "Failed to add dental care record. Please try again.");
    }
  };

  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const goBack = () => navigate("/horses");

 const startEdit = (v) => {
  setEditingId(v.id);
  setEditData({
    exam_date: v.exam_date ? v.exam_date.split("T")[0] : "",
    findings: v.findings || "",
    treatment: v.treatment || "",
    recommendations: v.recommendations || "",
    amount: v.Amount || ""
  });
};

  const saveEdit = async (id) => {
    try {
      // Ensure amount defaults to 0 if empty
      const editFormData = {
        ...editData,
        amount: editData.amount || "0.00"
      };
      
      await axios.put(getApiUrl(`/api/horses/${horseId}/dentalcare/${id}`), editFormData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setEditingId(null);
      fetchRecords();
      showSuccess("Dental care record updated successfully!");
    } catch (err) {
      console.error("Update error:", err);
      showError(err.response?.data?.error || "Failed to update dental care record. Please try again.");
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const handleDelete = async (recordId) => {
    try {
      await axios.delete(getApiUrl(`/api/dentalcare/${recordId}`), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      fetchRecords();
      showSuccess("Dental care record deleted successfully!");
    } catch (err) {
      console.error("Delete error:", err);
      showError(err.response?.data?.error || "Failed to delete dental care record. Please try again.");
    }
  };

  const [showForm, setShowForm] = useState(false);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      
      <button
        onClick={goBack}
        className="mb-4 px-4 py-2 bg-gray-100 border rounded hover:bg-gray-200 text-sm"
      >
  ← Back to Horses
</button>
      <h2 className="text-2xl font-bold mb-6 flex items-center">
        <Stethoscope className="mr-2 text-blue-600" /> Dental Care
      </h2>

 {!editingId && (
   <div className="bg-white shadow p-6 rounded mb-8">
     <div className="flex items-center justify-between mb-4">
       <h3 className="text-lg font-semibold">Add Dental Care Record</h3>
       <button
         onClick={() => setShowForm(!showForm)}
         className="text-sm text-blue-600 hover:underline focus:outline-none"
       >
         {showForm ? "Hide" : "Add"}
       </button>
     </div>

     {showForm && (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Exam Date *</label>
        <input
          type="date"
          name="exam_date"
          value={form.exam_date}
          onChange={handleChange}
          className="border px-3 py-2 rounded w-full"
          required
        />
        <p className="text-xs text-gray-500 mt-1">When the dental exam was performed</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Findings</label>
        <input
          name="findings"
          placeholder="e.g., Sharp points, Hooks, Wave mouth"
          value={form.findings}
          onChange={handleChange}
          className="border px-3 py-2 rounded w-full"
        />
        <p className="text-xs text-gray-500 mt-1">What was discovered during the exam</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Treatment</label>
        <input
          name="treatment"
          placeholder="e.g., Floating, Tooth extraction, Filing"
          value={form.treatment}
          onChange={handleChange}
          className="border px-3 py-2 rounded w-full"
        />
        <p className="text-xs text-gray-500 mt-1">What treatment was provided</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Recommendations</label>
        <input
          name="recommendations"
          placeholder="e.g., Recheck in 6 months, Monitor eating"
          value={form.recommendations}
          onChange={handleChange}
          className="border px-3 py-2 rounded w-full"
        />
        <p className="text-xs text-gray-500 mt-1">Follow-up recommendations</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Cost</label>
        <CurrencyInput
          name="amount"
          value={form.amount}
          onChange={(val) => setForm({ ...form, amount: val })}
          locale={navigator.language || 'en-US'}
          currency="USD"
          className="border px-3 py-2 rounded w-full"
          placeholder="0.00"
        />
        <p className="text-xs text-gray-500 mt-1">Dental care cost</p>
      </div>
    </div>
  )}

       {showForm && (
         <button
           onClick={handleSubmit}
           className="mt-4 bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
         >
           Save Dental Care
         </button>
       )}
     </div>
   )}


      <div className="bg-white shadow p-6 rounded">
        <h3 className="text-lg font-semibold mb-4">History</h3>
        {records.length === 0 ? (
          <p className="text-gray-500">No records yet.</p>
        ) : (
        <ul className="divide-y divide-gray-200">
          {records.map((rec) => (
            <li key={rec.id} className="py-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
              {editingId === rec.id ? (
                <div className="w-full sm:flex-grow">
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Exam Date *</label>
                    <input
                      type="date"
                      className="border px-2 py-1 rounded w-full"
                      name="exam_date"
                      value={editData.exam_date}
                      onChange={(e) => setEditData({ ...editData, exam_date: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Findings</label>
                    <input
                      className="border px-2 py-1 rounded w-full"
                      name="findings"
                      value={editData.findings}
                      onChange={(e) => setEditData({ ...editData, findings: e.target.value })}
                      placeholder="e.g., Sharp points, Hooks, Wave mouth"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Treatment</label>
                    <input
                      className="border px-2 py-1 rounded w-full"
                      name="treatment"
                      value={editData.treatment}
                      onChange={(e) => setEditData({ ...editData, treatment: e.target.value })}
                      placeholder="e.g., Floating, Tooth extraction, Filing"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Recommendations</label>
                    <input
                      className="border px-2 py-1 rounded w-full"
                      name="recommendations"
                      value={editData.recommendations}
                      onChange={(e) => setEditData({ ...editData, recommendations: e.target.value })}
                      placeholder="e.g., Recheck in 6 months, Monitor eating"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cost</label>
                    <CurrencyInput
                      name="amount"
                      value={editData.amount}
                      onChange={(val) => setEditData({ ...editData, amount: val })}
                      locale={navigator.language || 'en-US'}
                      currency="USD"
                      className="border px-2 py-1 rounded w-full"
                      placeholder="0.00"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => saveEdit(rec.id)}
                      className="bg-green-600 text-white px-4 py-1 rounded hover:bg-green-700"
                    >
                      Save
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="bg-gray-300 text-black px-4 py-1 rounded hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <p className="font-medium">Dental Exam - {formatWithSystemLocale(rec.exam_date)}</p>
                    {rec.findings && (
                      <p className="text-sm text-gray-600">
                        Findings: {rec.findings}
                      </p>
                    )}
                    {rec.treatment && (
                      <p className="text-sm text-gray-600">
                        Treatment: {rec.treatment}
                      </p>
                    )}
                    {rec.recommendations && (
                      <p className="text-sm text-gray-600">
                        Recommendations: {rec.recommendations}
                      </p>
                    )}
                    {rec.Amount && (
                      <p className="text-sm text-gray-500 italic">
                        Cost: <FormattedCurrency value={rec.Amount} />
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => startEdit(rec)}
                      className="text-blue-600 hover:text-blue-800 font-semibold"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(rec.id)}
                      className="text-red-600 hover:text-red-800 font-semibold"
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>

        )}
      </div>
    </div>
  );
}
